/**
 * TasksModuleGuard - Proteção de rota para o módulo de Tarefas
 * 
 * Verifica se o tenant atual possui o módulo de Tarefas habilitado (tasks_enabled).
 * Se não possuir, registra tentativa de bypass e redireciona para /tenant.
 * 
 * v2.0.0: além de tasks_enabled, aceita o parâmetro opcional `submodule`
 * ('retention' | 'legal'). Nesse caso também exige que o tenant tenha o
 * submódulo habilitado em Configurações › Tarefas (retention_enabled /
 * legal_enabled), registrando a tentativa em security_bypass_attempts.
 *
 * @version 2.0.0
 * RELEASE: 2026-08-21 v2.0.0 - Suporte aos submódulos Retenção e Jurídico
 * RELEASE: 2026-04-06 v1.0.0 - Criação do guard de tarefas
 */

import { ReactNode, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';

// Logger para auditoria de segurança do módulo de Tarefas
const tasksLogger = createLogger('TasksModuleGuard');

interface TasksModuleGuardProps {
  /** Conteúdo a ser renderizado se o módulo de Tarefas estiver habilitado */
  children: ReactNode;
  /**
   * Submódulo exigido além do módulo de Tarefas.
   * 'retention' → tenants.retention_enabled | 'legal' → tenants.legal_enabled
   */
  submodule?: 'retention' | 'legal';
}

/**
 * Registra tentativa de acesso ao módulo de Tarefas sem habilitação
 * na tabela security_bypass_attempts para auditoria
 */
const logTasksBypassAttempt = async (
  userId: string | undefined,
  tenantId: string | undefined,
  route: string
): Promise<void> => {
  try {
    if (!userId) return;

    let fullName: string | null = null;
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();
      fullName = profileData?.full_name || null;
    } catch {
      // Não bloquear se falhar busca do perfil
    }

    const { error } = await supabase
      .from('security_bypass_attempts')
      .insert({
        user_id: userId,
        attempted_tenant_id: tenantId || null,
        function_name: 'TasksModuleGuard',
        action: 'tasks_module_access_denied',
        attempted_value: route,
        user_agent: navigator.userAgent,
        details: {
          route,
          full_name: fullName,
          reason: 'tenant_tasks_not_enabled',
          timestamp: new Date().toISOString(),
        },
      });

    if (error) {
      tasksLogger.error('Falha ao registrar tentativa de bypass Tarefas', { error: error.message });
    } else {
      tasksLogger.warn('Tentativa de acesso ao Tarefas sem módulo habilitado registrada', {
        route,
        tenantId,
      });
    }
  } catch (err) {
    tasksLogger.error('Erro ao registrar bypass attempt Tarefas', { err });
  }
};

/**
 * Componente guard que bloqueia acesso às rotas de Tarefas
 * quando o tenant atual não possui tasks_enabled = true
 */
export function TasksModuleGuard({ children, submodule }: TasksModuleGuardProps) {
  const location = useLocation();
  const { user, currentTenant } = useAuth();

  // Ref para evitar múltiplos logs da mesma tentativa
  const hasLoggedBypass = useRef(false);

  // Verificar se o módulo de Tarefas está habilitado no tenant atual
  const tasksEnabledFlag = currentTenant?.tasks_enabled ?? false;

  // v2.0.0: flags dos submódulos (Retenção/Jurídico) — fonte única via RPC
  const { retentionEnabled, legalEnabled, isLoading: submoduleLoading } =
    useTasksModuleSettings();

  // Submódulo exigido pela rota (quando informado)
  const submoduleEnabled =
    submodule === 'retention' ? retentionEnabled : submodule === 'legal' ? legalEnabled : true;

  // Enquanto as flags do submódulo carregam, não bloquear (evita flicker/redirect indevido)
  const tasksEnabled =
    tasksEnabledFlag && (!submodule || submoduleLoading || submoduleEnabled);

  // Registrar tentativa de bypass quando módulo não está habilitado
  useEffect(() => {
    if (!tasksEnabled && !hasLoggedBypass.current && currentTenant) {
      hasLoggedBypass.current = true;
      logTasksBypassAttempt(user?.id, currentTenant?.id, location.pathname);
    }
  }, [tasksEnabled, user?.id, currentTenant, location.pathname]);

  // Se Tarefas não habilitado, redirecionar para dashboard principal
  if (!tasksEnabled) {
    // Aguarda o carregamento das flags do submódulo antes de decidir

    tasksLogger.warn('Acesso ao Tarefas bloqueado: módulo não habilitado para o tenant', {
      tenantId: currentTenant?.id,
      route: location.pathname,
    });
    return <Navigate to="/tenant" replace />;
  }

  // Módulo de Tarefas habilitado - renderizar conteúdo
  return <>{children}</>;
}
